import { dateTimeHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import moment from 'moment';
import { memo, useMemo } from 'react';
import { noValue } from 'shared/constants';
import { useCompanyBetaFeatures } from 'shared/hooks/useCompanyBetaFeatures';

import { BudgetParams as BudgetParamsWrapper } from '../../../BudgetParams/BudgetParams';
import { getCheckPeriodText } from './BudgetParams.helpers';
import { messages } from './BudgetParams.messages';
import { BudgetParamsProps } from './BudgetParams.types';

export const BudgetParams = memo<BudgetParamsProps>((props) => {
    const { budget, company } = props;
    const { isBudgetingZeroAmounts } = useCompanyBetaFeatures(company.id);

    const { isEnabled, constraints, origin, lastSyncDate, checkPeriod, zeroAmountHandlingType } = budget;

    const params = useMemo(() => {
        const result: { id: string; label: string; value: string }[] = [
            {
                id: 'status',
                label: messages.statusLabel,
                value: isEnabled ? messages.active : messages.deactivated,
            },
        ];

        constraints.forEach(({ fieldId, fieldName, value }) => {
            result.push({
                id: fieldId,
                label: fieldName,
                value: value?.text || noValue,
            });
        });

        if (origin === domain.BudgetOrigin.Manual) {
            result.push({
                id: 'dateOfUpload',
                label: messages.dateOfUploadLabel,
                value: dateTimeHelpers.formatDate(budget.modifiedDate),
            });
        }

        if (lastSyncDate) {
            result.push({
                id: 'lastSync',
                label: messages.lastSyncLabel,
                value: dateTimeHelpers.formatDate(lastSyncDate),
            });
        }

        if (checkPeriod) {
            result.push({
                id: 'checkPeriod',
                label: messages.checkPeriodLabel,
                value: getCheckPeriodText(budget),
            });
        }

        if (zeroAmountHandlingType && isBudgetingZeroAmounts) {
            result.push({
                id: 'zeroAmountHandlingType',
                label: messages.includeZeroAmountLabel,
                value:
                    zeroAmountHandlingType === domain.BudgetZeroAmountHandlingType.Consider
                        ? messages.zeroAmountConsider
                        : messages.zeroAmountNotConsider,
            });
        }

        if (budget.periodFrom && budget.periodTill) {
            const { year: periodFromYear, month: periodFromMonth } = budget.periodFrom;
            const { year: periodTillYear, month: periodTillMonth } = budget.periodTill;
            const periodFromStr = `${moment(periodFromMonth, 'M').format('MMM')} ${periodFromYear}`;
            const periodTillStr = `${moment(periodTillMonth, 'M').format('MMM')} ${periodTillYear}`;
            const value = periodFromStr === periodTillStr ? periodFromStr : `${periodFromStr} - ${periodTillStr}`;

            result.push({
                id: 'budgetPeriod',
                label: messages.budgetPeriod,
                value,
            });
        }

        return result;
    }, [
        budget,
        checkPeriod,
        constraints,
        isBudgetingZeroAmounts,
        isEnabled,
        lastSyncDate,
        origin,
        zeroAmountHandlingType,
    ]);

    return <BudgetParamsWrapper company={company} params={params} isBudgetDisabled={!isEnabled} />;
});

BudgetParams.displayName = 'BudgetParams';
